import { Controller } from "@hotwired/stimulus"
import { AvatarController, retargetClip } from "@primeta/persona-core"

export default class extends Controller {
  static targets = ["canvas", "loader", "orbitBtn"]
  static values = {
    persona: { type: String, default: "jin" },
    personaId: { type: Number, default: 0 },
    hasThumbnail: { type: Boolean, default: false },
    assetsUrl: { type: String, default: "" },
    interactive: { type: Boolean, default: true },
  }

  connect() {
    this.disposed = false
    this.animationId = null
    this.renderer = null
    this.controls = null
    this.resizeObserver = null
    this.THREE = null
    this._avatar = null
    this._animationMetadata = {}
    this._initialized = false

    this.init()
  }

  personaValueChanged() {
    if (!this._initialized || this._suppressSwap) return
    this.swapModel()
  }

  assetsUrlValueChanged() {
    if (!this._initialized || this._suppressSwap) return
    this.swapModel()
  }

  async swapModel() {
    this._loadGeneration = (this._loadGeneration || 0) + 1
    console.log("[avatar] swapModel gen=", this._loadGeneration, "assetsUrl=", this.assetsUrlValue)
    this._animationMetadata = {}
    await this.loadModel()
  }

  disconnect() {
    this.disposed = true
    this.element.vrmInstance = null
    this.element.avatarController = null

    if (this.animationId) cancelAnimationFrame(this.animationId)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.controls) this.controls.dispose()

    if (this.renderer?.domElement?.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
    }
    if (this.renderer) this.renderer.dispose()

    if (this._avatar?.vrm?.scene) {
      import('@pixiv/three-vrm').then(({ VRMUtils }) => {
        VRMUtils.deepDispose(this._avatar.vrm.scene)
      })
    }
  }

  async init() {
    const THREE = await import('three')
    this.THREE = THREE
    const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')
    await import('@pixiv/three-vrm')

    this._avatar = new AvatarController(THREE)

    if (this.disposed || !this.canvasTarget) return

    while (this.canvasTarget.firstChild) {
      this.canvasTarget.removeChild(this.canvasTarget.firstChild)
    }

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.canvasTarget.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()

    this.camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100)
    this.camera.position.set(0, 1.0, 4.5)
    this.camera.lookAt(0, 1.2, 0)

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.target.set(0, 1.2, 0)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.1
    this.controls.minDistance = 0.5
    this.controls.maxDistance = 5
    const interactive = this.interactiveValue
    this.controls.enablePan = interactive
    this.controls.enableRotate = interactive
    this.controls.enableZoom = interactive
    this.controls.zoomToCursor = true
    this.controls.screenSpacePanning = true
    // Left-click = drag (pan), Right-click = orbit (rotate)
    this.controls.mouseButtons = {
      LEFT: this.THREE.MOUSE.PAN,
      MIDDLE: this.THREE.MOUSE.DOLLY,
      RIGHT: this.THREE.MOUSE.ROTATE
    }
    this.controls.update()

    // Lighting: ambient + key + fill for well-lit models
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2))
    const key = new THREE.DirectionalLight(0xffffff, 1.0)
    key.position.set(1, 2, 2)
    this.scene.add(key)
    const fill = new THREE.DirectionalLight(0xffffff, 0.5)
    fill.position.set(-1, 0.5, 1)
    this.scene.add(fill)

    this.clock = new THREE.Clock()

    this.renderer.domElement.style.position = 'absolute'
    this.renderer.domElement.style.top = '0'
    this.renderer.domElement.style.left = '0'
    this.renderer.domElement.style.zIndex = '0'
    this.canvasTarget.style.position = 'relative'

    let resizeTimer
    const resize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        if (!this.canvasTarget || !this.renderer || !this.camera) return
        const { clientWidth, clientHeight } = this.canvasTarget
        if (clientWidth === 0 || clientHeight === 0) return
        this.renderer.setSize(clientWidth, clientHeight)
        this.camera.aspect = clientWidth / clientHeight
        this._fitCameraToModel()
        this.camera.updateProjectionMatrix()
      }, 50)
    }

    this.resizeObserver = new ResizeObserver(resize)
    this.resizeObserver.observe(this.canvasTarget)
    resize()

    await this.loadModel()
    this._initialized = true
    this.animate()
  }

  async loadModel() {
    const generation = this._loadGeneration || 0
    const THREE = this.THREE
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
    const { MeshoptDecoder } = await import('three/examples/jsm/libs/meshopt_decoder.module.js')
    const { VRMLoaderPlugin, VRMUtils } = await import('@pixiv/three-vrm')

    const loader = new GLTFLoader()
    loader.setMeshoptDecoder(MeshoptDecoder)
    loader.crossOrigin = 'anonymous'
    loader.register((parser) => new VRMLoaderPlugin(parser, { autoUpdateHumanBones: true }))

    // Check for local blob URL (file upload preview)
    let modelUrl = null
    this._assetData = null
    if (this.element._localBlobUrl) {
      modelUrl = this.element._localBlobUrl
      this.element._localBlobUrl = null
      // Use preview animation URL from data attribute if available
      const previewAnimUrl = this.element.dataset.avatarPreviewAnimationUrl
      if (previewAnimUrl) {
        this._assetData = {
          animation_urls: { "__idle": previewAnimUrl },
          animation_metadata: { "__idle": { loop_mode: "loop" } }
        }
        this._animationMetadata = this._assetData.animation_metadata
      }
    } else {
      // Fetch asset URLs from the persona assets endpoint
      const assetsUrl = this.assetsUrlValue || (this.personaIdValue ? `/personas/${this.personaIdValue}/assets` : null)
      if (assetsUrl && !assetsUrl.startsWith("blob:")) {
        try {
          const res = await fetch(assetsUrl)
          // Bail if a newer swap started while we were fetching
          if (generation !== (this._loadGeneration || 0)) return
          if (res.ok) {
            this._assetData = await res.json()
            if (generation !== (this._loadGeneration || 0)) return
            this._animationMetadata = this._assetData.animation_metadata || {}
            modelUrl = this._assetData.model_url
          }
        } catch { /* no assets available */ }
      }
    }

    if (!modelUrl) {
      console.warn('[persona] No model URL available for persona')
      return
    }

    if (generation !== (this._loadGeneration || 0)) return

    loader.load(modelUrl, async (gltf) => {
      if (this.disposed || generation !== (this._loadGeneration || 0)) return

      const loaded = gltf.userData.vrm
      if (!loaded) {
        // Plain GLB (non-VRM). Tag, clear old, add atomically.
        const oldGlbs = this.scene.children.filter(c => c.userData?.primetaModel)
        oldGlbs.forEach(child => {
          this.scene.remove(child)
          try { VRMUtils.deepDispose(child) } catch {}
        })
        gltf.scene.userData.primetaModel = true
        this.scene.add(gltf.scene)

        const box = new THREE.Box3().setFromObject(gltf.scene)
        this._modelBox = box
        this._fitCameraToModel()
        this._hideLoader()
        return
      }

      VRMUtils.removeUnnecessaryVertices(gltf.scene)
      VRMUtils.combineSkeletons(gltf.scene)
      if (VRMUtils.combineMorphs) VRMUtils.combineMorphs(loaded)

      // VRoid's default export often marks only the first joint of a
      // secondary-bone chain (hair, bust, tail) as a spring bone — leaving
      // Bust2/Hair_02/etc. static. Walk the tree and extend those partial
      // chains using the author's own physics settings so the whole chain
      // moves instead of just the root pivot.
      try {
        const { extendPartialSpringChains, fixupLookAtApplier } = await import('@primeta/persona-core')
        const added = extendPartialSpringChains(loaded)
        if (added > 0) console.log(`[avatar] extended ${added} spring bone chain(s)`)
        // Most VRoid 0.x files declare a Bone lookAt applier but don't ship
        // eye bones — silently preventing any eye motion. Swap to an
        // expression applier when look-* blendshapes are present.
        if (fixupLookAtApplier(loaded)) {
          console.log('[avatar] swapped lookAt applier: bone → expression')
        }
      } catch (err) {
        console.warn('[avatar] post-load VRM fixups failed:', err)
      }

      // Re-check generation in case a swap happened during the awaits above
      if (this.disposed || generation !== (this._loadGeneration || 0)) {
        try { VRMUtils.deepDispose(loaded.scene) } catch {}
        return
      }

      // Atomically remove ALL existing model scenes (VRM or plain GLB)
      const oldScenes = this.scene.children.filter(c => c.userData?.vrm || c.userData?.primetaModel)
      oldScenes.forEach(child => {
        this.scene.remove(child)
        try { VRMUtils.deepDispose(child) } catch {}
      })

      // Reset the avatar controller for the new model
      this._avatar = new AvatarController(this.THREE)

      loaded.scene.userData.vrm = loaded
      loaded.scene.userData.primetaModel = true
      loaded.scene.position.set(0, 0, 0)
      this.scene.add(loaded.scene)

      // Compute model bounding box for camera fitting
      const box = new THREE.Box3().setFromObject(loaded.scene)
      this._modelBox = box
      this._fitCameraToModel()

      const mixer = new THREE.AnimationMixer(loaded.scene)
      loaded.scene.traverse((obj) => { obj.frustumCulled = false })

      // Initialize the avatar controller with the loaded model
      this._avatar.initModel(loaded, this.clock, mixer)

      this.element.vrmInstance = loaded
      this.element.avatarController = this
      this._hideLoader()

      // Register embedded animations from the GLB
      for (const clip of gltf.animations) {
        const mode = this._animationMetadata?.[clip.name]?.loop_mode || 'loop'
        this._avatar.anims.registerAction(clip.name, clip, mode)
      }

      // rotateVRM0 before first render
      VRMUtils.rotateVRM0(loaded)

      // Pre-create the look-at proxy once so concurrent VRMA loads don't each
      // create their own (the auto-create path in createVRMAnimationClip races
      // and ends up adding multiple proxies to vrm.scene).
      if (loaded.lookAt) {
        const { VRMLookAtQuaternionProxy } = await import('@pixiv/three-vrm-animation')
        if (!loaded.scene.children.find(o => o instanceof VRMLookAtQuaternionProxy)) {
          const proxy = new VRMLookAtQuaternionProxy(loaded.lookAt)
          proxy.name = 'VRMLookAtQuaternionProxy'
          loaded.scene.add(proxy)
        }
      }

      // Auto-transition to idle when 'once_then_idle' animations finish
      mixer.addEventListener('finished', (e) => {
        for (const [name, action] of Object.entries(this._avatar.anims.actions)) {
          if (action === e.action) {
            const mode = this._avatar.anims.getLoopMode(name)
            if (mode === 'once_then_idle' && this._avatar.anims.actions.__idle) {
              this._avatar.anims.play('__idle')
            }
            break
          }
        }
      })

      // Always populate procedural bones — even when FBX animations load,
      // the breathing overlay writes to these every frame on top of the
      // mixer. Previously this was only called in the no-FBX fallback path,
      // so models with embedded/loaded animations had empty bones and no
      // visible breathing motion.
      this._avatar.setupProcedural()
      if (this._avatar.anims.hasAnimations && this._avatar.anims.actions.__idle) {
        this._avatar.anims.play('__idle')
      }

      // Load server-provided FBX/VRMA animations in the background
      this._loadFBXAnimations().then(() => {
        if (this.disposed || generation !== (this._loadGeneration || 0)) return
        if (!this._avatar.anims.hasAnimations) return

        // Play entrance animation if available, then transition to idle
        if (this._avatar.anims.actions.__entrance) {
          this._avatar.anims.play('__entrance', 'once_then_idle')
        } else if (this._avatar.anims.actions.__idle) {
          this._avatar.anims.play('__idle')
        }
      }).catch(err => console.warn('[persona] Background animation load failed:', err))

      this.dispatch("ready")
    }, undefined, (error) => {
      console.error('Failed to load VRM model:', error)
      this._hideLoader()
    })
  }

  // --- FBX Runtime Retargeting ---

  async _loadFBXAnimations() {
    const urls = this._assetData?.animation_urls
    if (!urls || Object.keys(urls).length === 0) return
    if (!this._avatar?.vrm) return

    const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js')
    const fbxLoader = new FBXLoader()

    const entries = Object.entries(urls)
    const loadPromises = entries.map(([name, url]) => {
      if (url.match(/\.vrma(\?|$)/i)) {
        return this._loadVRMAClip(url, name)
      }
      return new Promise((resolve) => {
        fbxLoader.load(url,
          (fbx) => {
            if (fbx.animations.length > 0) {
              const clip = retargetClip(this.THREE, fbx.animations[0], fbx, this._avatar.vrm)
              if (clip) { clip.name = name; resolve({ name, clip }); return }
            }
            resolve(null)
          },
          undefined,
          (err) => { console.warn(`[persona] Failed to load animation "${name}":`, err); resolve(null) }
        )
      })
    })
    const results = await Promise.all(loadPromises)
    for (const r of results) {
      if (r) {
        const mode = this._animationMetadata?.[r.name]?.loop_mode || 'loop'
        this._avatar.anims.registerAction(r.name, r.clip, mode)
      }
    }
  }

  _hideLoader() {
    const loader = this.element.querySelector('[data-avatar-target="loader"]')
    if (loader) {
      loader.style.opacity = '0'
      loader.style.transition = 'opacity 0.5s'
      setTimeout(() => loader.remove(), 500)
    }
  }

  // --- Camera fit-to-model ---

  _fitCameraToModel() {
    if (!this._modelBox || !this.camera || !this.canvasTarget) return

    const THREE = this.THREE
    const box = this._modelBox
    const size = new THREE.Vector3()
    box.getSize(size)

    const fov = this.camera.fov * (Math.PI / 180)

    // Fit to height only — let the model crop horizontally on narrow windows
    // rather than shrinking to fit width
    const z = (size.y / 2) / Math.tan(fov / 2) * 1.08

    const centerY = box.min.y + size.y / 2

    this.camera.position.set(0, centerY, z)
    this.camera.lookAt(0, centerY, 0)
    if (this.controls) {
      this.controls.target.set(0, centerY, 0)
      this.controls.enableZoom = this.interactiveValue
      this.controls.minDistance = z * 0.3
      this.controls.maxDistance = z * 3
      this.controls.update()
    }
  }

  // --- Camera controls ---

  zoomIn() {
    if (!this.controls) return
    this.camera.position.lerp(this.controls.target, 0.2)
    this.controls.update()
  }

  zoomOut() {
    if (!this.controls) return
    const dir = this.camera.position.clone().sub(this.controls.target).normalize()
    this.camera.position.addScaledVector(dir, this.camera.position.distanceTo(this.controls.target) * 0.25)
    this.controls.update()
  }

  resetCamera() {
    this._fitCameraToModel()
  }

  toggleOrbit() {
    if (!this.controls) return
    this.controls.enableRotate = !this.controls.enableRotate
    if (this.hasOrbitBtnTarget) {
      if (this.controls.enableRotate) {
        this.orbitBtnTarget.classList.add('!border-accent', '!text-accent')
      } else {
        this.orbitBtnTarget.classList.remove('!border-accent', '!text-accent')
      }
    }
  }

  // --- On-demand FBX loading (for previewing animations not in manifest) ---

  async loadAndPlayFBX(url, name) {
    return this.loadAndPlayAnimation(url, name)
  }

  async loadAndPlayAnimation(url, name) {
    if (this._avatar.anims.actions[name]) {
      this._avatar.anims.currentAnimName = ''
      this._avatar.anims.play(name)
      return
    }

    if (!this._avatar.vrm || !this._avatar.anims.mixer) return

    if (url.match(/\.vrma(\?|$)/i)) {
      await this._loadVRMA(url, name)
    } else if (url.match(/\.bvh(\?|$)/i)) {
      await this._loadBVH(url, name)
    } else {
      await this._loadFBX(url, name)
    }
  }

  async _loadVRMAClip(url, name) {
    try {
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
      const { VRMAnimationLoaderPlugin, createVRMAnimationClip } = await import('@pixiv/three-vrm-animation')
      const loader = new GLTFLoader()
      loader.register((parser) => new VRMAnimationLoaderPlugin(parser))
      return new Promise((resolve) => {
        loader.load(url, (gltf) => {
          const vrmAnims = gltf.userData.vrmAnimations
          if (vrmAnims && vrmAnims.length > 0) {
            const clip = createVRMAnimationClip(vrmAnims[0], this._avatar.vrm)
            clip.name = name
            resolve({ name, clip })
          } else { resolve(null) }
        }, undefined, (err) => {
          console.warn(`[persona] Failed to load VRMA "${name}":`, err)
          resolve(null)
        })
      })
    } catch (err) {
      console.warn(`[persona] VRMA import error for "${name}":`, err)
      return null
    }
  }

  async _loadVRMA(url, name) {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
    const { VRMAnimationLoaderPlugin, createVRMAnimationClip } = await import('@pixiv/three-vrm-animation')

    const loader = new GLTFLoader()
    loader.register((parser) => new VRMAnimationLoaderPlugin(parser))

    await new Promise((resolve, reject) => {
      loader.load(url, (gltf) => {
        const vrmAnimations = gltf.userData.vrmAnimations
        if (vrmAnimations && vrmAnimations.length > 0) {
          const clip = createVRMAnimationClip(vrmAnimations[0], this._avatar.vrm)
          clip.name = name
          this._avatar.anims.registerAction(name, clip)
          this._avatar.anims.currentAnimName = ''
          this._avatar.anims.play(name)
        }
        resolve()
      }, undefined, (err) => {
        console.error(`[persona] Failed to load VRMA from ${url}:`, err)
        reject(err)
      })
    })
  }

  async _loadFBX(url, name) {
    const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js')
    const fbxLoader = new FBXLoader()

    fbxLoader.load(url, (fbx) => {
      if (fbx.animations.length > 0) {
        const clip = retargetClip(this.THREE, fbx.animations[0], fbx, this._avatar.vrm)
        if (clip) {
          clip.name = name
          this._avatar.anims.registerAction(name, clip)
          this._avatar.anims.currentAnimName = ''
          this._avatar.anims.play(name)
        }
      }
    }, undefined, (err) => {
      console.error(`[persona] Failed to load FBX from ${url}:`, err)
    })
  }

  async _loadBVH(url, name) {
    const { BVHLoader } = await import('three/examples/jsm/loaders/BVHLoader.js')
    const loader = new BVHLoader()

    loader.load(url, (result) => {
      if (result.clip) {
        // BVH clips may use VRM bone names or need retargeting
        const clip = result.clip
        clip.name = name

        // Try to retarget if bone names don't match VRM
        const vrm = this._avatar.vrm
        const vrmBoneNames = new Set()
        if (vrm.humanoid) {
          for (const boneName of Object.values(vrm.humanoid.humanBones || {})) {
            const node = typeof boneName === 'object' ? boneName.node : boneName
            if (node?.name) vrmBoneNames.add(node.name)
          }
        }

        // Check if track names already match VRM bones
        const firstTrack = clip.tracks[0]
        const trackBone = firstTrack?.name?.split('.')[0]
        const needsRetarget = trackBone && !vrmBoneNames.has(trackBone)

        if (needsRetarget) {
          // Try retarget using our Mixamo mapper (BVH from VRM packs often use similar names)
          const retargeted = retargetClip(this.THREE, clip, result.skeleton, vrm)
          if (retargeted) {
            retargeted.name = name
            this._avatar.anims.registerAction(name, retargeted)
            this._avatar.anims.currentAnimName = ''
            this._avatar.anims.play(name)
            return
          }
        }

        // Direct use — bone names already match
        this._avatar.anims.registerAction(name, clip)
        this._avatar.anims.currentAnimName = ''
        this._avatar.anims.play(name)
      }
    }, undefined, (err) => {
      console.error(`[persona] Failed to load BVH from ${url}:`, err)
    })
  }

  // --- Public API (called by chat_controller and persona_preview_controller) ---

  setAnimation(trigger, intensity = 1.0) {
    this._avatar?.setAnimation(trigger, intensity)
  }

  setFacialEmotion(expression, intensity = 1.0) {
    this._avatar?.setEmotion(expression, intensity)
  }

  playAnimation(name, loopOverride, priority) {
    this._avatar?.playAnimation(name, loopOverride, priority)
  }

  // --- Thumbnail capture ---

  async captureThumbnail() {
    if (!this.renderer || !this.scene || !this.camera || !this.personaIdValue) return

    const { clientWidth, clientHeight } = this.canvasTarget
    const size = 512

    // Render at square size
    this.renderer.setSize(size, size)
    this.camera.aspect = 1
    this.camera.updateProjectionMatrix()
    this.renderer.render(this.scene, this.camera)

    // Composite: background + WebGL render
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')

    // Draw background first (await it fully before proceeding)
    const wrapper = this.element.parentElement
    const bgUrl = wrapper?.style?.backgroundImage?.match(/url\(["']?(.+?)["']?\)/)?.[1]
    if (bgUrl) {
      try {
        const res = await fetch(bgUrl)
        const blob = await res.blob()
        const bitmap = await createImageBitmap(blob)
        ctx.drawImage(bitmap, 0, 0, size, size)
      } catch {}
    }

    // Draw WebGL canvas on top
    ctx.drawImage(this.renderer.domElement, 0, 0, size, size)
    const dataUrl = canvas.toDataURL("image/png")

    // Restore
    this.renderer.setSize(clientWidth, clientHeight)
    this.camera.aspect = clientWidth / clientHeight
    this.camera.updateProjectionMatrix()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/personas/${this.personaIdValue}/thumbnail`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ thumbnail: dataUrl })
    }).catch(() => {})
  }

  _autoCaptureThumbnail() {
    this.hasThumbnailValue = true
    this.captureThumbnail()
  }

  // --- Render loop ---

  animate() {
    if (this.disposed) return
    this.animationId = requestAnimationFrame(() => this.animate())

    const delta = this.clock.getDelta()
    if (this.controls) this.controls.update()

    this._avatar?.update(delta)

    // Smooth camera follow — track hips X position only (preserve zoom/depth)
    const hipsPos = this._avatar?.hipsWorldPos
    if (hipsPos && this.controls) {
      const lerp = 0.08
      this.controls.target.x += (hipsPos.x - this.controls.target.x) * lerp
      this.controls.target.z += (hipsPos.z - this.controls.target.z) * lerp
    }

    this.renderer.render(this.scene, this.camera)
  }
}
